import React, { FC, useEffect, useRef, useState } from 'react';
import ReactResizeDetector from 'react-resize-detector';
import { useHistory } from 'react-router-dom';
import { Card, Divider, Space, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { AuditLogSearchDto } from '@base/backend/dist/lib/models/dtos/audit-log.search.dto';

import EnvironmentSelect from '@lib/components/env-select';
import PagingAgGrid, { PagingAgGridRef } from '@lib/components/paging-grid/paging-grid';
import { ColDef, ServerSideStoreType } from '@lib/components/ui/ag-grid';
import { DatePicker } from '@lib/components/ui/date-picker';
import { PagingOptions } from '@lib/services/base/paginate';
import { auditLogService } from '@lib/services/cstool';
import dayjs, { DATE_TIME_FORMAT } from '@lib/utils/dayjs';
import { useQuery } from '@lib/utils/use-query';

interface AuditLog {
  id?: number;
  environment: string;
  userEmail: string;
  action: string;
  playerId: string;
  category?: string;
  createdAt?: string;
}

const colDefs: ColDef[] = [
  {
    headerName: '#',
    field: 'id',
    minWidth: 60,
    maxWidth: 100,
    sort: 'desc',
    pinned: 'left',
  },
  {
    headerName: 'Player ID',
    field: 'playerId',
    minWidth: 200,
    maxWidth: 200,
    resizable: true,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Action',
    field: 'action',
    minWidth: 200,
    resizable: true,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Admin',
    field: 'userEmail',
    minWidth: 160,
    maxWidth: 250,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Category',
    field: 'category',
    minWidth: 150,
    maxWidth: 150,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Date created',
    field: 'createdAt',
    cellRendererFramework: ({ value }: any) =>
      value ? (
        <Tooltip title={dayjs(value).format(DATE_TIME_FORMAT)}>{dayjs(value).fromNow()}</Tooltip>
      ) : (
        '-'
      ),
    minWidth: 150,
    maxWidth: 150,
  },
  {
    headerName: 'Environment',
    field: 'environment',
    minWidth: 150,
    maxWidth: 150,
  },
];

const AuditLogScreen: FC = () => {
  const history = useHistory();
  const query = useQuery();

  const gridRef = useRef<PagingAgGridRef>(null);
  const reloadTimerRef = useRef<NodeJS.Timeout>();

  const [environment, setEnvironment] = useState<string>();
  const [from, setFrom] = useState<string>();
  const [to, setTo] = useState<string>();

  const pagingOptionsRef = useRef<PagingOptions<AuditLogSearchDto>['where']>({});

  const updateQuery = () => {
    const queries = [
      environment && `environment=${environment}`,
      from && `from=${from}`,
      to && `to=${to}`,
    ]
      .filter(Boolean)
      .join('&');

    history.push({
      pathname: history.location.pathname,
      search: `?${queries}`,
    });
  };

  const scheduleNextReload = () => {
    clearTimeout(reloadTimerRef.current as any);
    reloadTimerRef.current = setTimeout(() => {
      if (gridRef.current) {
        gridRef.current.fetch();
      }
      scheduleNextReload();
    }, 1000 * 20);
  };

  useEffect(() => {
    if (query.get('environment')) {
      setEnvironment(query.get('environment') ?? undefined);
    }
    setFrom(
      query.get('from') ?? new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
    );
    if (query.get('to')) {
      setTo(query.get('to') ?? undefined);
    }
  }, []);

  useEffect(() => {
    scheduleNextReload();
    return () => clearTimeout(reloadTimerRef.current as any);
  }, []);

  useEffect(() => {
    if (pagingOptionsRef.current) {
      pagingOptionsRef.current.environment = environment;
      pagingOptionsRef.current.from = from;
      pagingOptionsRef.current.to = to;
    }
    if (gridRef.current) {
      gridRef.current.fetch();
    }
    updateQuery();
  }, [environment, from, to]);

  return (
    <Card
      className="flex flex-col flex-1 min-h-full"
      bordered={false}
      bodyStyle={{ padding: 16, display: 'flex', flexDirection: 'column', flex: '1 1 100%' }}
    >
      <div className="flex items-center pb-4">
        <span className="ml-auto">
          <Space>
            <EnvironmentSelect
              value={pagingOptionsRef.current?.environment as string}
              onChange={setEnvironment}
              allowClear
              style={{ minWidth: 200 }}
            />
            <DatePicker
              bordered
              showTime
              autoFocus
              style={{ width: '100%' }}
              utcTime={false}
              value={from}
              onChange={(value) => setFrom(value || undefined)}
            />
            <DatePicker
              bordered
              showTime
              autoFocus
              style={{ width: '100%' }}
              utcTime={false}
              value={to}
              onChange={(value) => setTo(value || undefined)}
            />
          </Space>
          <Divider type="vertical" />
          <ReloadOutlined onClick={() => gridRef.current?.fetch()} />
        </span>
      </div>

      <ReactResizeDetector
        handleHeight
        render={({ height }) => (
          <div className="flex-auto ag-theme-alpine bordered" style={{ height }}>
            <PagingAgGrid<AuditLog>
              request={auditLogService.paging}
              requestParams={{ where: pagingOptionsRef.current }}
              actionRef={gridRef}
              serverSideStoreType={ServerSideStoreType.Partial}
              defaultColDef={{
                sortable: true,
                floatingFilter: true,
                suppressMenu: true,
                tooltipValueGetter: ({ valueFormatted }) => valueFormatted,
              }}
              columnDefs={[...colDefs]}
              enableBrowserTooltips
              pagination
              paginationPageSize={25}
            />
          </div>
        )}
      />
    </Card>
  );
};

export default AuditLogScreen;
